// -----------------------------------------------------------------------------
// ISSUES TRACKER — pure helpers (no React, no network) so they are unit-tested
// in tests/unit/issues-logic.test.ts. The DATABASE owns the business rules
// (numbering, never-delete, void, overdue); these only shape data for display.
// -----------------------------------------------------------------------------
import type { IssueFields, IssueRow, IssueStatus } from "../../core/api/types";

/** Statuses that count as "open" (rule R4 in 0002_issues.sql). */
export const OPEN_STATUSES: IssueStatus[] = ["Open", "Critical"];
export const isOpen = (s: IssueStatus): boolean => OPEN_STATUSES.includes(s);

/** Statuses a user may pick in the status dropdown. Voided is only reachable through "Void issue". */
export const EDITABLE_STATUSES: IssueStatus[] = ["Open", "Critical", "Closed"];

/** Whole-number percentage, safe for 0 totals. */
export const pct = (part: number, whole: number): number => (whole > 0 ? Math.round((part / whole) * 100) : 0);

/** Row 1 status cards (A3, A4, P0): key, label, colours. On A4 they are filters (code list #4). */
export type StatusKey = "open" | "critical" | "closed" | "overdue" | "total" | "all";
export const STATUS_CARDS: { key: StatusKey; label: string; bg: string; fg: string }[] = [
  { key: "open", label: "Open", bg: "#FAEEDA", fg: "#854F0B" },
  { key: "critical", label: "Critical", bg: "#FAECE7", fg: "#712B13" },
  { key: "closed", label: "Closed", bg: "#EAF3DE", fg: "#3B6D11" },
  { key: "overdue", label: "Overdue", bg: "#FCEBEB", fg: "#A32D2D" },
  { key: "total", label: "Total", bg: "#f6f4ef", fg: "#1a1a18" },
  { key: "all", label: "Total incl. voided", bg: "#f6f4ef", fg: "#5f5e5a" },
];
export const MAX_DISCIPLINES = 7;

/** Does a row match a status card? (overdue comes from the database: days past target, Open/Critical only) */
export function matchesStatusCard(r: IssueRow, key: StatusKey | ""): boolean {
  switch (key) {
    case "": case "all": return true;
    case "open": return r.status === "Open";
    case "critical": return r.status === "Critical";
    case "closed": return r.status === "Closed";
    case "overdue": return r.overdue_days > 0;
    case "total": return r.status !== "Voided";
  }
}

/** A4 filters. Cards: status (row 1) + discipline (row 2) combine. Target: overdue or a date range. */
export interface IssueFilter {
  status: StatusKey | ""; discipline: string;
  target: "" | "overdue" | "range"; from: string | null; to: string | null;
  actionBy: string; q: string;
}
export const EMPTY_FILTER: IssueFilter = { status: "", discipline: "", target: "", from: null, to: null, actionBy: "", q: "" };

/** Filter rows on the Component summary (A4). Search: code, description, location, gridline. */
export function filterIssues(rows: IssueRow[], f: IssueFilter): IssueRow[] {
  const q = f.q.trim().toLowerCase();
  return rows.filter((r) =>
    matchesStatusCard(r, f.status) &&
    // discipline card alone = that discipline's OPEN issues (what the card counts); with a status card = that status
    (!f.discipline || (r.discipline === f.discipline && (f.status !== "" || isOpen(r.status)))) &&
    (f.target !== "overdue" || r.overdue_days > 0) &&
    (f.target !== "range" || (!!r.target_date && (!f.from || r.target_date >= f.from) && (!f.to || r.target_date <= f.to))) &&
    (!f.actionBy || (f.actionBy === "–" ? !r.latest_action_by : r.latest_action_by === f.actionBy)) &&
    (!q || [r.code, r.description, r.location, r.gridline].some((t) => (t ?? "").toLowerCase().includes(q))));
}

/** Is any A4 filter on? ("Clear filters" shows only then — comment #6) */
export const isFiltered = (f: IssueFilter): boolean => JSON.stringify(f) !== JSON.stringify(EMPTY_FILTER);

/**
 * Migrate preview (A4.3): which ticked issues move (Open / Critical, in issue order) and their new codes,
 * counting up from the target's next code (e.g. HZ-10, HZ-11). The database does the real numbering.
 */
export function migrationPlan(picked: IssueRow[], nextCode: string | null): { pairs: [string, string][]; skipped: number } {
  const moving = [...picked].filter((r) => isOpen(r.status)).sort((a, b) => a.seq - b.seq);
  const m = nextCode ? /^(.*)-(\d+)$/.exec(nextCode) : null;
  const pairs = m ? moving.map((r, k): [string, string] => [r.code, `${m[1]}-${String(Number(m[2]) + k).padStart(2, "0")}`]) : [];
  return { pairs, skipped: picked.length - moving.length };
}

/** Clicking a card: same card again clears it; another card in the same row switches (code list #4). */
export const toggleCard = <T extends string>(current: T | "", clicked: T): T | "" => (current === clicked ? "" : clicked);

/** Sort by issue number (Issue ↕). */
export const sortRows = (rows: IssueRow[], dir: "asc" | "desc"): IssueRow[] =>
  [...rows].sort((a, b) => (dir === "asc" ? a.seq - b.seq : b.seq - a.seq));

/** Count rows per status card (used where the API stats are not at hand, e.g. tests). */
export function countByStatus(rows: IssueRow[]): Record<StatusKey, number> {
  const c: Record<StatusKey, number> = { open: 0, critical: 0, closed: 0, overdue: 0, total: 0, all: 0 };
  for (const r of rows) for (const k of Object.keys(c) as StatusKey[]) if (matchesStatusCard(r, k)) c[k]++;
  return c;
}

/** "14 days overdue" text (A4 Overdue column shows "14 days"). */
export const overdueText = (days: number, long = false): string =>
  days > 0 ? `${days} ${days === 1 ? "day" : "days"}${long ? " overdue" : ""}` : "–";

/** Only the fields that changed — keeps issues_update calls small and activity log accurate. */
export function changedFields(before: IssueFields, after: IssueFields): Partial<IssueFields> {
  const out: Partial<IssueFields> = {};
  (Object.keys(after) as (keyof IssueFields)[]).forEach((k) => {
    if ((before[k] ?? null) !== (after[k] ?? null)) (out as Record<string, unknown>)[k] = after[k];
  });
  return out;
}

/** Client-side check before sending a new issue (the database re-checks everything). */
export function validateNewIssue(v: { component?: string; discipline: string; description: string }): string | null {
  if (v.component !== undefined && !v.component) return "Pick a component";
  if (!v.discipline) return "Pick a discipline";
  if (!v.description.trim()) return "Describe the issue";
  return null;
}

/** ISO date + n days (default target date for a new issue = today + 14). */
export function addDays(iso: string, n: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** A5 "Issue [n] of N": the issue code for a typed number, or null if out of range. */
export function codeForNumber(prefix: string, n: number, count: number): string | null {
  return Number.isInteger(n) && n >= 1 && n <= count ? `${prefix}-${String(n).padStart(2, "0")}` : null;
}
